import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Box from '@mui/material/Box';
import { Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

import { getMissions } from 'src/api/apis';
import { type Mission } from '../../utils/type';
import { getCurrentUser } from 'src/utils/local';
import { TokenExpiredError } from 'src/api/error';
import { ROLES, ROUTES } from '../../utils/constant';
import PageLayout from 'src/components/Layout/PageLayout';
import MissionCard from 'src/components/Card/MissionCard';
import SearchInput from 'src/components/Input/SearchInput';
import AddMissionDialog from '../../components/Dialog/AddMission';
import EditMissionDialog from '../../components/Dialog/EditMission';

export default function MissionPage () {
  const { t } = useTranslation();
  const history = useHistory();
  const currentUser = getCurrentUser();
  const currentRole = currentUser?.role;
  const isAdmin = currentRole === ROLES.departmentAdmin || currentRole === ROLES.platformAdmin;
  const [missions, setMissions] = useState<Mission[]>([]);
  const [selectedMissionIndex, setSelectedMissionIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [showAddMissionDialog, setShowAddMissionDialog] = useState(false);
  const [showEditMissionDialog, setShowEditMissionDialog] = useState(false);
  const showMissions = missions.filter(mission => mission.title.en.includes(search));

  const getMissionList = () => {
    getMissions()
      .then(response => {
        if (response.data.status === '1') setMissions(response.data.mission);
      })
      .catch(err => {
        if (err instanceof TokenExpiredError) {
          history.push(ROUTES.login);
        }
      });
  };

  useEffect(() => {
    getMissionList();
  }, []);

  const handleAddMission = () => {
    setShowAddMissionDialog(true);
  };

  const handleSuccessAddMission = () => {
    setShowAddMissionDialog(false);
    getMissionList();
  };

  const handleSuccessEditMission = () => {
    setShowEditMissionDialog(false);
    getMissionList();
  };

  const handleEdit = (index: number) => {
    setSelectedMissionIndex(index);
    setShowEditMissionDialog(true);
  };

  return (
    <PageLayout
      topRight={
        <div style={{ display: 'flex', gap: '20px' }}>
          <SearchInput onTextChange={text => { setSearch(text); }} />
          {isAdmin && (
            <Button
              variant="contained"
              style={{ width: '155px', height: '35px', fontSize: '18px', padding: '6px 10px' }}
              onClick={handleAddMission}
              startIcon={<AddIcon />}
            >
              {t('Add mission')}
            </Button>
          )}
        </div>
      }
      title={t('Mission')}
      isAdmin={isAdmin}
    >
      <Box sx={{ paddingTop: '30px' }}>
        {showMissions.map((mission, index) => (
          <MissionCard mission={mission} key={mission.id} allowEdit={isAdmin} onClickEdit={() => { handleEdit(index); }} />
        ))}
      </Box>
      <AddMissionDialog
        show={showAddMissionDialog}
        onClose={() => { setShowAddMissionDialog(false); }}
        onSuccess={handleSuccessAddMission}
      />
      <EditMissionDialog
        show={showEditMissionDialog}
        onClose={() => { setShowEditMissionDialog(false); }}
        onSuccess={handleSuccessEditMission}
        mission={missions[selectedMissionIndex]}
      />
    </PageLayout>
  );
}
